import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type Konva from 'konva';
import { Arrow, Circle, Group, Layer, Line, Rect, Shape, Stage, Text } from 'react-konva';
import type { AnchorRef, CanvasElement, ConnectorElement } from '@archer/shared';
import { DEFAULT_GRID_SIZE, DEFAULT_SHAPE_STROKE, DEFAULT_SHAPE_STROKE_WIDTH } from '@archer/shared';
import { useCanvasStore, type ToolKind } from './store';
import { getConnectorPoints, getShapeAnchor, hitTest, snap } from './shapes/geometry';
import { getShapeLayers } from './shapes/definitions';

interface CanvasBoardProps {
  width: number;
  height: number;
}

const SHAPE_DEFAULTS: Record<ToolKind, Partial<CanvasElement>> = {
  select: {},
  pan: {},
  rect: { type: 'rect' },
  ellipse: { type: 'ellipse' },
  diamond: { type: 'diamond' },
  parallelogram: { type: 'parallelogram' },
  trapezoid: { type: 'trapezoid' },
  cylinder: { type: 'cylinder' },
  document: { type: 'document' },
  person: { type: 'person' },
  note: { type: 'note' },
  text: { type: 'text' },
  arrow: { type: 'arrow' },
  line: { type: 'line' },
};

const GridLayer = ({ width, height, viewport, showGrid }: { width: number; height: number; viewport: { x: number; y: number; scale: number }; showGrid: boolean }) => {
  if (!showGrid) return null;
  const size = DEFAULT_GRID_SIZE * viewport.scale;
  const offsetX = ((viewport.x % size) + size) % size;
  const offsetY = ((viewport.y % size) + size) % size;
  const lines: React.ReactElement[] = [];
  for (let x = offsetX; x < width; x += size) {
    lines.push(
      <Line key={`grid-v-${x}`} points={[x, 0, x, height]} stroke="#1e293b" strokeWidth={1} listening={false} />,
    );
  }
  for (let y = offsetY; y < height; y += size) {
    lines.push(
      <Line key={`grid-h-${y}`} points={[0, y, width, y]} stroke="#1e293b" strokeWidth={1} listening={false} />,
    );
  }
  return (
    <Layer listening={false}>
      <Rect x={0} y={0} width={width} height={height} fill="#0b1220" listening={false} />
      {lines}
    </Layer>
  );
};

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLE_SIZE = 8;
const DEFAULT_FONT_SIZE = 40;

const ElementNode = ({
  el,
  elements,
  tool,
  selectedIds,
  onSelect,
  onEditText,
  onDragStart,
  onDragMove,
  onDragEnd,
}: {
  el: CanvasElement;
  elements: CanvasElement[];
  tool: ToolKind;
  selectedIds: string[];
  onSelect: (id: string, additive: boolean) => void;
  onEditText: (id: string) => void;
  onDragStart: (id: string, pos: { x: number; y: number }) => void;
  onDragMove: (id: string, pos: { x: number; y: number }) => void;
  onDragEnd: (id: string, pos: { x: number; y: number }) => void;
}) => {
  const isConnector = el.type === 'arrow' || el.type === 'line';
  const isSelected = selectedIds.includes(el.id);
  const additiveKey = (e: Konva.KonvaEventObject<MouseEvent | DragEvent>) =>
    e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (tool === 'select') {
      e.cancelBubble = true;
      onSelect(el.id, additiveKey(e));
    }
  };
  const handleDblClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    onEditText(el.id);
  };
  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (tool !== 'select') return;
    if (!isSelected) {
      onSelect(el.id, additiveKey(e));
    }
    onDragStart(el.id, { x: e.target.x(), y: e.target.y() });
  };
  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (tool !== 'select') return;
    onDragMove(el.id, { x: e.target.x(), y: e.target.y() });
  };
  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (tool !== 'select') return;
    onDragEnd(el.id, { x: e.target.x(), y: e.target.y() });
  };

  if (isConnector) {
    const c = el as ConnectorElement;
    const connectorStroke =
      c.stroke && c.stroke !== '#000000' && c.stroke.toLowerCase() !== '#1f2937'
        ? c.stroke
        : '#e2e8f0';
    const pts = getConnectorPoints(c, elements);
    const startArrow = c.startArrow ?? false;
    const endArrow = c.endArrow ?? (el.type === 'arrow');
    if (!startArrow && !endArrow) {
      return (
        <Line
          id={el.id}
          points={pts}
          stroke={connectorStroke}
          strokeWidth={c.strokeWidth ?? DEFAULT_SHAPE_STROKE_WIDTH}
          dash={c.dash}
          lineCap="round"
          listening
          onMouseDown={handleMouseDown}
        />
      );
    }
    return (
      <Arrow
        id={el.id}
        points={pts}
        stroke={connectorStroke}
        strokeWidth={c.strokeWidth ?? DEFAULT_SHAPE_STROKE_WIDTH}
        fill={connectorStroke}
        dash={c.dash}
        pointerAtBeginning={startArrow}
        pointerLength={endArrow ? 10 : 0}
        pointerWidth={endArrow ? 10 : 0}
        listening
        perfectDrawEnabled={false}
        onMouseDown={handleMouseDown}
      />
    );
  }

  return (
    <Group
      id={el.id}
      x={el.x}
      y={el.y}
      width={el.width}
      height={el.height}
      rotation={el.rotation ?? 0}
      draggable={tool === 'select'}
      onMouseDown={handleMouseDown}
      onDblClick={handleDblClick}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      perfectDrawEnabled={false}
    >
      <ShapePath el={el} />
      {el.text && (
        <Text
          x={el.textPadding ?? 6}
          y={el.textPadding ?? 6}
          width={Math.max(0, el.width - (el.textPadding ?? 6) * 2)}
          height={Math.max(0, el.height - (el.textPadding ?? 6) * 2)}
          text={el.text}
          fontSize={el.fontSize ?? DEFAULT_FONT_SIZE}
          fontFamily={el.fontFamily ?? 'Inter, system-ui, sans-serif'}
          fontStyle={el.fontStyle}
          fill={el.textColor ?? '#0f172a'}
          align={el.textAlign ?? 'center'}
          verticalAlign={el.verticalAlign ?? 'middle'}
          listening={false}
        />
      )}
    </Group>
  );
};

// Darken a hex color by `amount` (0-1). Falls back to the original color for non-hex values.
const darkenColor = (hex: string, amount: number): string => {
  const m = /^#([0-9a-f]{3,6})$/i.exec(hex);
  if (!m) return hex;
  const [, color] = m;
  if (!color) return hex;
  let c = color;
  if (c.length === 3) {
    c = c
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }
  const r = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(0, 2), 16) * (1 - amount))));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(2, 4), 16) * (1 - amount))));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(c.slice(4, 6), 16) * (1 - amount))));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

// Inflate the hit area slightly so shapes can be grabbed near their edge.
const HIT_PADDING = 8;

/**
 * Paints every shape type from the single geometry source of truth in `shapes/definitions.ts`.
 * Each layer's SVG `d` string is handed straight to the browser's native `Path2D` (fully
 * spec-compliant arc/curve math - no more hand-rolled `ctx.ellipse`/`ctx.arc` calls to get
 * wrong), so what renders here is exactly what `lib/export.ts` emits into SVG.
 */
const ShapePath = ({ el }: { el: CanvasElement }) => {
  const fill = el.fill ?? '#ffffff';
  const stroke = el.stroke ?? DEFAULT_SHAPE_STROKE;
  const sw = el.strokeWidth ?? DEFAULT_SHAPE_STROKE_WIDTH;
  return (
    <Shape
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      shadowBlur={0}
      sceneFunc={(ctx) => {
        for (const layer of getShapeLayers(el.type, el.width, el.height)) {
          const path = new Path2D(layer.d);
          if (layer.fill !== 'none') {
            ctx.fillStyle = layer.fill === 'dark' ? darkenColor(fill, 0.1) : fill;
            ctx.fill(path);
          }
          if (layer.stroke) {
            ctx.strokeStyle = stroke;
            ctx.lineWidth = sw;
            ctx.stroke(path);
          }
        }
      }}
      hitFunc={(ctx, shape) => {
        // Grow the shape by HIT_PADDING on every side, then translate the padded-size
        // geometry back into place, so the clickable region extends past the visible edge.
        ctx.save();
        ctx.translate(-HIT_PADDING, -HIT_PADDING);
        ctx.fillStyle = shape.colorKey;
        for (const layer of getShapeLayers(el.type, el.width + HIT_PADDING * 2, el.height + HIT_PADDING * 2)) {
          ctx.fill(new Path2D(layer.d));
        }
        ctx.restore();
      }}
    />
  );
};

const MIN_CONNECTOR_LENGTH = 24;

const ConnectorOverlay = ({
  el,
  elements,
  viewport,
  onEndpointStart,
  onMoveStart,
}: {
  el: ConnectorElement;
  elements: CanvasElement[];
  viewport: { x: number; y: number; scale: number };
  onEndpointStart: (which: 'source' | 'target', world: { x: number; y: number }) => void;
  onMoveStart: (world: { x: number; y: number }) => void;
}) => {
  const pts = getConnectorPoints(el, elements);
  const x1 = pts[0] ?? 0;
  const y1 = pts[1] ?? 0;
  const x2 = pts[pts.length - 2] ?? x1;
  const y2 = pts[pts.length - 1] ?? y1;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const r = 5 / viewport.scale;
  const strokeWidth = 1.5 / viewport.scale;

  const toWorld = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage?.();
    const pos = stage?.getPointerPosition();
    if (!pos) return { x: 0, y: 0 };
    return { x: (pos.x - viewport.x) / viewport.scale, y: (pos.y - viewport.y) / viewport.scale };
  };

  return (
    <Group>
      <Line
        points={pts}
        stroke="#3D9168"
        strokeWidth={2 / viewport.scale}
        dash={[6 / viewport.scale, 4 / viewport.scale]}
        opacity={0.5}
        listening={false}
      />
      <Circle
        x={x1}
        y={y1}
        radius={r}
        fill="#ffffff"
        stroke="#3D9168"
        strokeWidth={strokeWidth}
        onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true;
          onEndpointStart('source', toWorld(e));
        }}
      />
      <Circle
        x={x2}
        y={y2}
        radius={r}
        fill="#ffffff"
        stroke="#3D9168"
        strokeWidth={strokeWidth}
        onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true;
          onEndpointStart('target', toWorld(e));
        }}
      />
      <Circle
        x={midX}
        y={midY}
        radius={r}
        fill="#3D9168"
        stroke="#3D9168"
        strokeWidth={strokeWidth}
        onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
          e.cancelBubble = true;
          onMoveStart(toWorld(e));
        }}
      />
    </Group>
  );
};

const TextEditor = ({
  el,
  viewport,
  onDone,
  onCancel,
}: {
  el: CanvasElement;
  viewport: { x: number; y: number; scale: number };
  onDone: (text: string) => void;
  onCancel: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.innerText = el.text ?? '';
    node.focus();
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [el.text]);

  const padding = (el.textPadding ?? 6) * viewport.scale;
  const style: CSSProperties = {
    position: 'absolute',
    left: viewport.x + el.x * viewport.scale,
    top: viewport.y + el.y * viewport.scale,
    width: Math.max(0, el.width * viewport.scale),
    height: Math.max(0, el.height * viewport.scale),
    padding,
    fontSize: (el.fontSize ?? DEFAULT_FONT_SIZE) * viewport.scale,
    fontFamily: el.fontFamily ?? 'Inter, system-ui, sans-serif',
    color: el.textColor ?? '#0f172a',
    fontWeight: el.fontStyle?.includes('bold') ? 'bold' : 'normal',
    fontStyle: el.fontStyle?.includes('italic') ? 'italic' : 'normal',
    textAlign: el.textAlign ?? 'center',
    display: 'flex',
    alignItems:
      el.verticalAlign === 'top'
        ? 'flex-start'
        : el.verticalAlign === 'bottom'
          ? 'flex-end'
          : 'center',
    justifyContent:
      el.textAlign === 'left'
        ? 'flex-start'
        : el.textAlign === 'right'
          ? 'flex-end'
          : 'center',
    overflow: 'hidden',
    outline: 'none',
    background: 'transparent',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: '1.2',
    zIndex: 20,
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      style={style}
      onBlur={() => {
        if (!cancelledRef.current) {
          onDone(ref.current?.innerText ?? '');
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelledRef.current = true;
          onCancel();
        }
      }}
    />
  );
};

const SelectionOverlay = ({
  el,
  viewport,
  onResizeStart,
}: {
  el: CanvasElement;
  viewport: { x: number; y: number; scale: number };
  onResizeStart: (handle: ResizeHandle, world: { x: number; y: number }) => void;
}) => {
  const handleSize = HANDLE_SIZE / viewport.scale;
  const offset = handleSize / 2;
  const handles: { key: ResizeHandle; cx: number; cy: number }[] = [
    { key: 'nw', cx: 0, cy: 0 },
    { key: 'n', cx: el.width / 2, cy: 0 },
    { key: 'ne', cx: el.width, cy: 0 },
    { key: 'e', cx: el.width, cy: el.height / 2 },
    { key: 'se', cx: el.width, cy: el.height },
    { key: 's', cx: el.width / 2, cy: el.height },
    { key: 'sw', cx: 0, cy: el.height },
    { key: 'w', cx: 0, cy: el.height / 2 },
  ];

  return (
    <Group x={el.x} y={el.y}>
      <Rect
        x={-2 / viewport.scale}
        y={-2 / viewport.scale}
        width={el.width + 4 / viewport.scale}
        height={el.height + 4 / viewport.scale}
        stroke="#3D9168"
        dash={[6 / viewport.scale, 4 / viewport.scale]}
        strokeWidth={1.5 / viewport.scale}
        listening={false}
      />
      {handles.map(({ key, cx, cy }) => (
        <Rect
          key={key}
          x={cx - offset}
          y={cy - offset}
          width={handleSize}
          height={handleSize}
          fill="#ffffff"
          stroke="#3D9168"
          strokeWidth={1 / viewport.scale}
          onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
            e.cancelBubble = true;
            const stage = e.target.getStage?.();
            const pos = stage?.getPointerPosition();
            if (!pos) return;
            onResizeStart(key, {
              x: (pos.x - viewport.x) / viewport.scale,
              y: (pos.y - viewport.y) / viewport.scale,
            });
          }}
        />
      ))}
    </Group>
  );
};

export const CanvasBoard = ({ width, height }: CanvasBoardProps) => {
  const stageRef = useRef<Konva.Stage | null>(null);
  const elements = useCanvasStore((s) => s.elements);
  const selectedIds = useCanvasStore((s) => s.selectedIds);
  const tool = useCanvasStore((s) => s.tool);
  const viewport = useCanvasStore((s) => s.viewport);
  const showGrid = useCanvasStore((s) => s.showGrid);
  const snapToGrid = useCanvasStore((s) => s.snapToGrid);
  const setViewport = useCanvasStore((s) => s.setViewport);
  const setTool = useCanvasStore((s) => s.setTool);
  const setSelected = useCanvasStore((s) => s.setSelected);
  const clearSelection = useCanvasStore((s) => s.clearSelection);
  const toggleSelected = useCanvasStore((s) => s.toggleSelected);
  const updateElement = useCanvasStore((s) => s.updateElement);
  const addElement = useCanvasStore((s) => s.addElement);

  const idCounter = useRef(0);
  const newId = useCallback((prefix: string) => {
    const t = Date.now().toString(36);
    const r = Math.random().toString(36).slice(2, 8);
    const s = (idCounter.current++).toString(36);
    return `${prefix}_${t}_${r}_${s}`;
  }, []);

  const screenToWorld = useCallback(
    (sx: number, sy: number) => ({
      x: (sx - viewport.x) / viewport.scale,
      y: (sy - viewport.y) / viewport.scale,
    }),
    [viewport],
  );

  const getWorldPos = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>): { x: number; y: number } | null => {
      const stage = e.target.getStage?.();
      const pos = stage?.getPointerPosition();
      if (!pos) return null;
      return screenToWorld(pos.x, pos.y);
    },
    [screenToWorld],
  );

  const getScreenPos = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>): { x: number; y: number } | null => {
      const stage = e.target.getStage?.();
      return stage?.getPointerPosition() ?? null;
    },
    [],
  );

  const hitElement = useCallback(
    (pt: { x: number; y: number }) => {
      for (let i = elements.length - 1; i >= 0; i--) {
        const el = elements[i];
        if (el && hitTest(el, pt)) return el.id;
      }
      return undefined;
    },
    [elements],
  );

  const elementBounds = (el: CanvasElement): { x: number; y: number; width: number; height: number } => {
    if (el.type === 'arrow' || el.type === 'line') {
      const pts = (el as ConnectorElement).points;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < pts.length; i += 2) {
        minX = Math.min(minX, pts[i]!);
        maxX = Math.max(maxX, pts[i]!);
        minY = Math.min(minY, pts[i + 1]!);
        maxY = Math.max(maxY, pts[i + 1]!);
      }
      return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
    }
    return el;
  };

  const rectsOverlap = (
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number },
  ): boolean =>
    a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

  const getIdsInRect = useCallback(
    (start: { x: number; y: number }, end: { x: number; y: number }) => {
      const rect = {
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        width: Math.abs(end.x - start.x),
        height: Math.abs(end.y - start.y),
      };
      const ids: string[] = [];
      for (const el of elements) {
        if (rectsOverlap(elementBounds(el), rect)) ids.push(el.id);
      }
      return ids;
    },
    [elements],
  );

  const findNearestShapeAnchor = useCallback(
    (
      pt: { x: number; y: number },
      threshold = 20,
    ): { el: CanvasElement; anchor: AnchorRef['anchor']; point: { x: number; y: number } } | undefined => {
      let best:
        | { el: CanvasElement; anchor: AnchorRef['anchor']; point: { x: number; y: number }; dist: number }
        | undefined;
      for (const el of elements) {
        if (el.type === 'arrow' || el.type === 'line') continue;
        const anchors: AnchorRef['anchor'][] = ['top', 'right', 'bottom', 'left'];
        for (const a of anchors) {
          const ap = getShapeAnchor(el, a, pt);
          const d = Math.hypot(ap.x - pt.x, ap.y - pt.y);
          if (d < threshold && (!best || d < best.dist)) {
            best = { el, anchor: a, point: ap, dist: d };
          }
        }
      }
      return best ? { el: best.el, anchor: best.anchor, point: best.point } : undefined;
    },
    [elements],
  );

  const handleElementDragStart = useCallback((id: string, pos: { x: number; y: number }) => {
    const state = useCanvasStore.getState();
    const ids = state.selectedIds.includes(id) ? state.selectedIds : [id];
    const startPositions = new Map<string, { x: number; y: number }>();
    for (const sid of ids) {
      const el = state.elements.find((e) => e.id === sid);
      if (el) startPositions.set(sid, { x: el.x, y: el.y });
    }
    dragState.current = { start: pos, startPositions };
  }, []);

  const handleElementDragMove = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      if (!dragState.current) return;
      const dx = pos.x - dragState.current.start.x;
      const dy = pos.y - dragState.current.start.y;
      for (const [sid, sp] of dragState.current.startPositions) {
        const el = elements.find((e) => e.id === sid);
        if (!el || el.type === 'arrow' || el.type === 'line') continue;
        updateElement(sid, { x: sp.x + dx, y: sp.y + dy });
      }
    },
    [elements, updateElement],
  );

  const handleElementDragEnd = useCallback(
    (id: string, pos: { x: number; y: number }) => {
      if (!dragState.current) return;
      const grid = snapToGrid ? DEFAULT_GRID_SIZE : 1;
      const dx = pos.x - dragState.current.start.x;
      const dy = pos.y - dragState.current.start.y;
      for (const [sid, sp] of dragState.current.startPositions) {
        const el = elements.find((e) => e.id === sid);
        if (!el || el.type === 'arrow' || el.type === 'line') continue;
        updateElement(sid, { x: snap(sp.x + dx, grid), y: snap(sp.y + dy, grid) });
      }
      dragState.current = null;
    },
    [snapToGrid, elements, updateElement],
  );

  const draftRef = useRef<{
    id: string;
    startWorld: { x: number; y: number };
    kind: 'shape' | 'connector';
    sourceAnchor?: { elementId: string; anchor: AnchorRef['anchor'] };
    targetAnchor?: { elementId: string; anchor: AnchorRef['anchor'] };
  } | null>(null);

  const panState = useRef<{
    active: boolean;
    startScreenX: number;
    startScreenY: number;
    startViewportX: number;
    startViewportY: number;
  } | null>(null);

  const resizeState = useRef<{
    handle: ResizeHandle;
    elId: string;
    startWorld: { x: number; y: number };
    startBounds: { x: number; y: number; width: number; height: number };
  } | null>(null);

  const connectorEditState = useRef<{
    mode: 'source' | 'target' | 'move';
    elId: string;
    startWorld: { x: number; y: number };
    startPoints: number[];
  } | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const selectionState = useRef<{
    active: boolean;
    startWorld: { x: number; y: number };
    currentWorld: { x: number; y: number };
    additive: boolean;
    initialIds: string[];
  } | null>(null);

  const dragState = useRef<{
    start: { x: number; y: number };
    startPositions: Map<string, { x: number; y: number }>;
  } | null>(null);

  // Fallback drag driven from the Stage instead of Konva's native
  // per-node drag events — see the comment in onStageMouseDown for why.
  const manualDragActive = useRef(false);
  const lastManualDragWorld = useRef<{ x: number; y: number } | null>(null);

  const editingElement = useMemo(() => {
    if (!editingId) return undefined;
    return elements.find((e) => e.id === editingId);
  }, [editingId, elements]);

  const startEditing = useCallback((id: string) => {
    if (tool !== 'select') return;
    setEditingId(id);
  }, [tool]);

  const finishEditing = useCallback(
    (text: string) => {
      if (!editingId) return;
      const trimmed = text.replace(/\n$/, '');
      updateElement(editingId, { text: trimmed });
      setEditingId(null);
    },
    [editingId, updateElement],
  );

  const cancelEditing = useCallback(() => {
    setEditingId(null);
  }, []);

  const startDraft = useCallback(
    (world: { x: number; y: number }) => {
      const grid = snapToGrid ? DEFAULT_GRID_SIZE : 1;
      const snapFn = (v: number) => (snapToGrid ? snap(v, grid) : v);
      const sx = snapFn(world.x);
      const sy = snapFn(world.y);

      if (tool === 'arrow' || tool === 'line') {
        const id = newId('el');
        const source = findNearestShapeAnchor(world, 20);
        const startPt = source ? source.point : { x: sx, y: sy };
        draftRef.current = {
          id,
          startWorld: startPt,
          kind: 'connector',
          sourceAnchor: source ? { elementId: source.el.id, anchor: source.anchor } : undefined,
        };
        addElement({
          id,
          type: tool,
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          rotation: 0,
          stroke: '#e2e8f0',
          strokeWidth: DEFAULT_SHAPE_STROKE_WIDTH,
          points: [startPt.x, startPt.y, startPt.x, startPt.y],
          source: source ? { elementId: source.el.id, anchor: source.anchor } : undefined,
          startArrow: false,
          endArrow: tool === 'arrow',
        } as ConnectorElement);
        return;
      }

      const defaults = SHAPE_DEFAULTS[tool];
      if (defaults.type) {
        const id = newId('el');
        draftRef.current = { id, startWorld: { x: sx, y: sy }, kind: 'shape' };
        addElement({
          id,
          type: defaults.type as CanvasElement['type'],
          x: sx,
          y: sy,
          width: 1,
          height: 1,
          rotation: 0,
          fill: '#ffffff',
          stroke: DEFAULT_SHAPE_STROKE,
          strokeWidth: DEFAULT_SHAPE_STROKE_WIDTH,
          text: defaults.type === 'text' ? 'Text' : defaults.type === 'person' ? 'User' : '',
          fontSize: DEFAULT_FONT_SIZE,
          textColor: '#0f172a',
        });
      }
    },
    [tool, snapToGrid, newId, addElement, findNearestShapeAnchor],
  );

  const onStageMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const evt = e.evt;
      if (evt.button !== 0 && evt.button !== 1) return;

      // Pan tool / middle mouse / alt-drag always pans.
      if (tool === 'pan' || evt.altKey || evt.button === 1) {
        const pos = getScreenPos(e);
        if (!pos) return;
        panState.current = {
          active: true,
          startScreenX: pos.x,
          startScreenY: pos.y,
          startViewportX: viewport.x,
          startViewportY: viewport.y,
        };
        setIsPanning(true);
        return;
      }

      // Clicks on shapes/selection handles are handled by their own Konva events.
      if (e.target !== e.currentTarget) return;

      // Background click in select tool: start a selection rectangle.
      if (tool === 'select') {
        const world = getWorldPos(e);
        if (!world) return;
        const additive = evt.shiftKey || evt.ctrlKey || evt.metaKey;

        // Konva's hit-testing for our custom Path2D shapes renders them to a
        // hidden canvas and reads back the pixel color under the pointer
        // (via context.getImageData) to find the topmost shape. Some
        // browsers/privacy settings — notably Brave's fingerprinting
        // protection — farble or block that readback, so Konva reports
        // *every* click as missing all shapes and hitting the Stage
        // background, even when it visually landed on one (only rubber-band
        // select still works, since that's plain geometry math, not canvas
        // readback). We only reach this branch when Konva itself believes
        // this was a background click, so re-check with our own
        // pure-geometry hit test before assuming that's actually true.
        const hitId = hitElement(world);
        if (hitId) {
          if (!useCanvasStore.getState().selectedIds.includes(hitId)) {
            toggleSelected(hitId, additive);
          }
          handleElementDragStart(hitId, world);
          manualDragActive.current = true;
          lastManualDragWorld.current = world;
          return;
        }

        if (!additive) clearSelection();
        selectionState.current = {
          active: true,
          startWorld: world,
          currentWorld: world,
          additive,
          initialIds: additive ? [...selectedIds] : [],
        };
        return;
      }

      // Drawing tool on empty background. Connectors may start on a shape to
      // snap to its anchor.
      const world = getWorldPos(e);
      if (!world) return;
      if (tool !== 'arrow' && tool !== 'line') {
        const hitId = hitElement(world);
        if (hitId) return;
      }
      startDraft(world);
    },
    [
      tool,
      viewport,
      getScreenPos,
      getWorldPos,
      hitElement,
      clearSelection,
      startDraft,
      selectedIds,
      toggleSelected,
      handleElementDragStart,
    ],
  );

  const onStageMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (selectionState.current?.active) {
        const world = getWorldPos(e);
        if (!world) return;
        selectionState.current.currentWorld = world;
        const ids = getIdsInRect(selectionState.current.startWorld, world);
        const next = new Set(selectionState.current.additive ? selectionState.current.initialIds : ids);
        for (const id of ids) next.add(id);
        setSelected(Array.from(next));
        return;
      }

      if (panState.current?.active) {
        const pos = getScreenPos(e);
        if (!pos) return;
        const dx = pos.x - panState.current.startScreenX;
        const dy = pos.y - panState.current.startScreenY;
        setViewport({
          x: panState.current.startViewportX + dx,
          y: panState.current.startViewportY + dy,
          scale: viewport.scale,
        });
        return;
      }

      if (resizeState.current) {
        const world = getWorldPos(e);
        if (!world) return;
        const { handle, elId, startWorld, startBounds } = resizeState.current;
        const dx = world.x - startWorld.x;
        const dy = world.y - startWorld.y;
        let { x, y, width, height } = startBounds;

        if (handle.includes('e')) width = Math.max(40, startBounds.width + dx);
        if (handle.includes('w')) {
          width = Math.max(40, startBounds.width - dx);
          x = startBounds.x + startBounds.width - width;
        }
        if (handle.includes('s')) height = Math.max(24, startBounds.height + dy);
        if (handle.includes('n')) {
          height = Math.max(24, startBounds.height - dy);
          y = startBounds.y + startBounds.height - height;
        }

        const grid = snapToGrid ? DEFAULT_GRID_SIZE : 1;
        if (snapToGrid) {
          x = snap(x, grid);
          y = snap(y, grid);
          width = Math.max(40, snap(width, grid));
          height = Math.max(24, snap(height, grid));
        }

        updateElement(elId, { x, y, width, height });
        return;
      }

      if (connectorEditState.current) {
        const world = getWorldPos(e);
        if (!world) return;
        const s = connectorEditState.current;
        const dx = world.x - s.startWorld.x;
        const dy = world.y - s.startWorld.y;
        const pts = [...s.startPoints];
        if (s.mode === 'source') {
          const nx = pts[0]! + dx;
          const ny = pts[1]! + dy;
          const snapAnchor = findNearestShapeAnchor({ x: nx, y: ny }, 20);
          pts[0] = snapAnchor ? snapAnchor.point.x : nx;
          pts[1] = snapAnchor ? snapAnchor.point.y : ny;
          updateElement(s.elId, {
            points: pts,
            source: snapAnchor ? { elementId: snapAnchor.el.id, anchor: snapAnchor.anchor } : undefined,
          } as Partial<CanvasElement>);
        } else if (s.mode === 'target') {
          const n = pts.length;
          const nx = pts[n - 2]! + dx;
          const ny = pts[n - 1]! + dy;
          const snapAnchor = findNearestShapeAnchor({ x: nx, y: ny }, 20);
          pts[n - 2] = snapAnchor ? snapAnchor.point.x : nx;
          pts[n - 1] = snapAnchor ? snapAnchor.point.y : ny;
          updateElement(s.elId, {
            points: pts,
            target: snapAnchor ? { elementId: snapAnchor.el.id, anchor: snapAnchor.anchor } : undefined,
          } as Partial<CanvasElement>);
        } else if (s.mode === 'move') {
          for (let i = 0; i < pts.length; i += 2) {
            pts[i]! += dx;
            pts[i + 1]! += dy;
          }
          updateElement(s.elId, {
            points: pts,
            source: undefined,
            target: undefined,
          } as Partial<CanvasElement>);
        }
        return;
      }

      if (manualDragActive.current) {
        const world = getWorldPos(e);
        if (!world) return;
        lastManualDragWorld.current = world;
        handleElementDragMove('', world);
        return;
      }

      const draft = draftRef.current;
      if (!draft) return;
      const world = getWorldPos(e);
      if (!world) return;

      const grid = snapToGrid ? DEFAULT_GRID_SIZE : 1;
      const cx = snapToGrid ? snap(world.x, grid) : world.x;
      const cy = snapToGrid ? snap(world.y, grid) : world.y;
      const { x: sx, y: sy } = draft.startWorld;

      if (draft.kind === 'connector') {
        const target = findNearestShapeAnchor({ x: cx, y: cy }, 20);
        const endPt = target ? target.point : { x: cx, y: cy };
        draftRef.current = { ...draft, targetAnchor: target ? { elementId: target.el.id, anchor: target.anchor } : undefined };
        updateElement(draft.id, { points: [sx, sy, endPt.x, endPt.y] } as Partial<CanvasElement>);
      } else {
        updateElement(draft.id, {
          x: Math.min(sx, cx),
          y: Math.min(sy, cy),
          width: Math.max(2, Math.abs(cx - sx)),
          height: Math.max(2, Math.abs(cy - sy)),
        });
      }
    },
    [
      getScreenPos,
      getWorldPos,
      setViewport,
      viewport.scale,
      snapToGrid,
      updateElement,
      findNearestShapeAnchor,
      getIdsInRect,
      setSelected,
      handleElementDragMove,
    ],
  );

  const finalizeDraft = useCallback(() => {
    const draft = draftRef.current;
    if (!draft) return;
    const { snapToGrid, elements: currentEls } = useCanvasStore.getState();
    const el = currentEls.find((e) => e.id === draft.id);
    if (el && el.type !== 'arrow' && el.type !== 'line') {
      const minW = 40;
      const minH = 24;
      if (el.width < minW || el.height < minH) {
        const grid = snapToGrid ? DEFAULT_GRID_SIZE : 1;
        const cx = el.x + el.width / 2;
        const cy = el.y + el.height / 2;
        const w = snapToGrid ? snap(minW, grid) : minW;
        const h = snapToGrid ? snap(minH, grid) : minH;
        updateElement(el.id, {
          x: snapToGrid ? snap(cx - w / 2, grid) : cx - w / 2,
          y: snapToGrid ? snap(cy - h / 2, grid) : cy - h / 2,
          width: w,
          height: h,
        });
      }
    }
    if (el && (el.type === 'arrow' || el.type === 'line')) {
      const c = el as ConnectorElement;
      const pts = [...c.points];
      // Enforce a minimum length so tiny drags don't produce arrowhead-only shapes.
      const dx = pts[2]! - pts[0]!;
      const dy = pts[3]! - pts[1]!;
      const len = Math.hypot(dx, dy);
      if (len < MIN_CONNECTOR_LENGTH) {
        const dir =
          len < 0.5
            ? { x: MIN_CONNECTOR_LENGTH, y: 0 }
            : { x: (dx / len) * MIN_CONNECTOR_LENGTH, y: (dy / len) * MIN_CONNECTOR_LENGTH };
        pts[2] = pts[0]! + dir.x;
        pts[3] = pts[1]! + dir.y;
      }
      const end = { x: pts[pts.length - 2]!, y: pts[pts.length - 1]! };
      const target = findNearestShapeAnchor(end, 20);
      const updates: Partial<ConnectorElement> = { points: pts };
      if (target) {
        updates.target = { elementId: target.el.id, anchor: target.anchor };
        pts[pts.length - 2] = target.point.x;
        pts[pts.length - 1] = target.point.y;
        updates.points = pts;
      }
      updateElement(el.id, updates);
    }
    // Switch to select tool and select the newly created shape so it can be
    // moved and resized immediately.
    setTool('select');
    setSelected([draft.id]);
    draftRef.current = null;
  }, [setTool, setSelected, updateElement, findNearestShapeAnchor]);

  const onStageMouseUp = useCallback(() => {
    if (selectionState.current?.active) {
      selectionState.current = null;
      return;
    }
    if (panState.current?.active) {
      panState.current = null;
      setIsPanning(false);
      return;
    }
    if (resizeState.current) {
      resizeState.current = null;
      return;
    }
    if (connectorEditState.current) {
      connectorEditState.current = null;
      return;
    }
    if (manualDragActive.current) {
      manualDragActive.current = false;
      if (lastManualDragWorld.current) {
        handleElementDragEnd('', lastManualDragWorld.current);
      }
      lastManualDragWorld.current = null;
      return;
    }
    finalizeDraft();
  }, [finalizeDraft, handleElementDragEnd]);

  const onWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();
      const stage = e.target.getStage();
      if (!stage) return;
      const pointer = stage.getPointerPosition();
      if (!pointer) return;
      const scaleBy = 1.05;
      const oldScale = viewport.scale;
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const newScale = Math.max(0.2, Math.min(4, direction > 0 ? oldScale * scaleBy : oldScale / scaleBy));
      const mousePointTo = {
        x: (pointer.x - viewport.x) / oldScale,
        y: (pointer.y - viewport.y) / oldScale,
      };
      setViewport({
        scale: newScale,
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      });
    },
    [viewport, setViewport],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      )
        return;
      if ((e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const state = useCanvasStore.getState();
        setSelected(state.elements.map((el) => el.id));
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const ids = useCanvasStore.getState().selectedIds;
        if (ids.length) {
          useCanvasStore.getState().removeElements(ids);
          e.preventDefault();
        }
      }
      if (e.key === 'Escape') {
        clearSelection();
      }
      if ((e.key === 'Enter' || e.key === 'F2') && !editingId) {
        const state = useCanvasStore.getState();
        if (state.selectedIds.length === 1) {
          const id = state.selectedIds[0];
          if (!id) return;
          const el = state.elements.find((el2) => el2.id === id);
          if (el && el.type !== 'arrow' && el.type !== 'line') {
            setEditingId(id);
            e.preventDefault();
          }
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearSelection, editingId]);

  // Safety net: finalize any active interaction if the pointer is released
  // outside the stage.
  useEffect(() => {
    const onWindowMouseUp = () => {
      if (selectionState.current?.active) {
        selectionState.current = null;
      }
      if (panState.current?.active) {
        panState.current = null;
        setIsPanning(false);
      }
      if (resizeState.current) resizeState.current = null;
      if (connectorEditState.current) connectorEditState.current = null;
      finalizeDraft();
    };
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, [finalizeDraft]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const selectedShapeForResize = useMemo(() => {
    if (selectedIds.length !== 1) return undefined;
    const el = elements.find((e) => e.id === selectedIds[0]);
    if (!el || el.type === 'arrow' || el.type === 'line') return undefined;
    return el;
  }, [selectedIds, elements]);

  const selectedConnector = useMemo(() => {
    if (selectedIds.length !== 1) return undefined;
    const el = elements.find((e) => e.id === selectedIds[0]);
    if (!el || (el.type !== 'arrow' && el.type !== 'line')) return undefined;
    return el as ConnectorElement;
  }, [selectedIds, elements]);

  return (
    <div className="relative h-full w-full">
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        onWheel={onWheel}
        onMouseDown={onStageMouseDown}
        onMouseMove={onStageMouseMove}
        onMouseUp={onStageMouseUp}
        style={{
          cursor: isPanning
            ? 'grabbing'
            : tool === 'pan'
              ? 'grab'
              : tool === 'select'
                ? 'default'
                : 'crosshair',
          touchAction: 'none',
        }}
      >
        <GridLayer width={width} height={height} viewport={viewport} showGrid={showGrid} />
        <Layer x={viewport.x} y={viewport.y} scaleX={viewport.scale} scaleY={viewport.scale}>
          {elements.map((el) => (
            <ElementNode
              key={el.id}
              el={el}
              elements={elements}
              tool={tool}
              selectedIds={selectedIds}
              onSelect={toggleSelected}
              onEditText={startEditing}
              onDragStart={handleElementDragStart}
              onDragMove={handleElementDragMove}
              onDragEnd={handleElementDragEnd}
            />
          ))}
          {selectionState.current?.active && (
            <Rect
              x={Math.min(selectionState.current.startWorld.x, selectionState.current.currentWorld.x)}
              y={Math.min(selectionState.current.startWorld.y, selectionState.current.currentWorld.y)}
              width={Math.abs(selectionState.current.currentWorld.x - selectionState.current.startWorld.x)}
              height={Math.abs(selectionState.current.currentWorld.y - selectionState.current.startWorld.y)}
              fill="rgba(61, 145, 104, 0.08)"
              stroke="#3D9168"
              dash={[6, 4]}
              listening={false}
            />
          )}
          {selectedConnector && (
            <ConnectorOverlay
              key={`conn-sel-${selectedConnector.id}`}
              el={selectedConnector}
              elements={elements}
              viewport={viewport}
              onEndpointStart={(which, world) => {
                connectorEditState.current = {
                  mode: which,
                  elId: selectedConnector.id,
                  startWorld: world,
                  startPoints: getConnectorPoints(selectedConnector, elements),
                };
              }}
              onMoveStart={(world) => {
                connectorEditState.current = {
                  mode: 'move',
                  elId: selectedConnector.id,
                  startWorld: world,
                  startPoints: getConnectorPoints(selectedConnector, elements),
                };
              }}
            />
          )}
          {selectedShapeForResize && (
            <SelectionOverlay
              key={`sel-${selectedShapeForResize.id}`}
              el={selectedShapeForResize}
              viewport={viewport}
              onResizeStart={(handle, world) => {
                resizeState.current = {
                  handle,
                  elId: selectedShapeForResize.id,
                  startWorld: world,
                  startBounds: {
                    x: selectedShapeForResize.x,
                    y: selectedShapeForResize.y,
                    width: selectedShapeForResize.width,
                    height: selectedShapeForResize.height,
                  },
                };
              }}
            />
          )}
        </Layer>
      </Stage>
      {selectedConnector && (
        <div className="pointer-events-auto absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-slate-700 bg-slate-900/90 px-2 py-1 text-xs text-slate-200 shadow-soft backdrop-blur">
          <button
            className={`rounded px-2 py-1 hover:bg-slate-800 ${(selectedConnector.startArrow ?? false) ? 'bg-slate-700' : ''}`}
            onClick={() =>
              updateElement(selectedConnector.id, { startArrow: !(selectedConnector.startArrow ?? false) })
            }
          >
            Start arrow
          </button>
          <button
            className={`rounded px-2 py-1 hover:bg-slate-800 ${(selectedConnector.endArrow ?? selectedConnector.type === 'arrow') ? 'bg-slate-700' : ''}`}
            onClick={() =>
              updateElement(selectedConnector.id, { endArrow: !(selectedConnector.endArrow ?? selectedConnector.type === 'arrow') })
            }
          >
            End arrow
          </button>
          <button
            className="rounded px-2 py-1 hover:bg-slate-800"
            onClick={() => {
              const dash = selectedConnector.dash;
              let nextDash: number[] | undefined;
              if (!dash || dash.length === 0) nextDash = [6, 4];
              else if (dash[0] === 6 && dash[1] === 4) nextDash = [2, 4];
              else nextDash = undefined;
              updateElement(selectedConnector.id, { dash: nextDash });
            }}
          >
            {(() => {
              const dash = selectedConnector.dash;
              if (!dash || dash.length === 0) return 'Solid';
              if (dash[0] === 6 && dash[1] === 4) return 'Dashed';
              return 'Dotted';
            })()}
          </button>
        </div>
      )}
      {editingElement && (
        <TextEditor
          key={`editor-${editingElement.id}`}
          el={editingElement}
          viewport={viewport}
          onDone={finishEditing}
          onCancel={cancelEditing}
        />
      )}
      {selectedShapeForResize && !editingElement && (
        <div className="pointer-events-auto absolute left-1/2 top-4 z-10 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/90 px-2 py-1 text-xs text-slate-200 shadow-soft backdrop-blur">
          <label className="flex items-center gap-1">
            Size
            <input
              type="number"
              min={6}
              max={96}
              value={selectedShapeForResize.fontSize ?? DEFAULT_FONT_SIZE}
              onChange={(e) =>
                updateElement(selectedShapeForResize.id, {
                  fontSize: Math.max(6, Math.min(96, Number(e.target.value) || DEFAULT_FONT_SIZE)),
                })
              }
              className="w-12 rounded border border-slate-600 bg-slate-800 px-1 py-0.5 text-right"
            />
          </label>
          <input
            type="color"
            value={selectedShapeForResize.textColor ?? '#0f172a'}
            onChange={(e) => updateElement(selectedShapeForResize.id, { textColor: e.target.value })}
            className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
            title="Text color"
          />
          <button
            className={`rounded px-2 py-1 hover:bg-slate-800 ${selectedShapeForResize.fontStyle?.includes('bold') ? 'bg-slate-700' : ''}`}
            onClick={() => {
              const hasBold = selectedShapeForResize.fontStyle?.includes('bold') ?? false;
              const hasItalic = selectedShapeForResize.fontStyle?.includes('italic') ?? false;
              const next = hasBold
                ? hasItalic
                  ? 'italic'
                  : 'normal'
                : hasItalic
                  ? 'bold italic'
                  : 'bold';
              updateElement(selectedShapeForResize.id, { fontStyle: next });
            }}
          >
            B
          </button>
          <button
            className={`rounded px-2 py-1 hover:bg-slate-800 ${selectedShapeForResize.fontStyle?.includes('italic') ? 'bg-slate-700' : ''}`}
            onClick={() => {
              const hasBold = selectedShapeForResize.fontStyle?.includes('bold') ?? false;
              const hasItalic = selectedShapeForResize.fontStyle?.includes('italic') ?? false;
              const next = hasItalic
                ? hasBold
                  ? 'bold'
                  : 'normal'
                : hasBold
                  ? 'bold italic'
                  : 'italic';
              updateElement(selectedShapeForResize.id, { fontStyle: next });
            }}
          >
            I
          </button>
          {(['left', 'center', 'right'] as const).map((align) => (
            <button
              key={align}
              className={`rounded px-2 py-1 capitalize hover:bg-slate-800 ${(selectedShapeForResize.textAlign ?? 'center') === align ? 'bg-slate-700' : ''}`}
              onClick={() => updateElement(selectedShapeForResize.id, { textAlign: align })}
            >
              {align}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
